import { useState, useEffect } from "react";
import { trackOnce } from "../lib/analytics";
import { usePageMeta } from "../lib/page-meta";
import { useSearchParams } from "react-router-dom";
import { T } from "../tokens";
import { JobCard }           from "../components/JobCard";
import { Badge }             from "../components/Badge";
import { MatchDetailDrawer } from "../components/MatchDetailDrawer";
import { ResumeClarityCallout } from "../components/ResumeClarityCallout";
import { MatchReadinessPanel } from "../components/MatchReadinessPanel";
import { useMatchesPage, useApplyMutation, useSaveMutation, useGenerateMatches } from "../hooks/useMatches";
import { Btn } from "../components/Btn";
import { useActiveResume } from "../hooks/useResume";
import { anyFieldIncludes } from "../lib/text";
import { pageWindow, pageCount, clampPage } from "../lib/pagination";
import type { MatchWithJob } from "@jobreel/shared";

const WORK_AUTH_OPTIONS = [
  { label: "All",                  value: "" },
  { label: "Remote (India)",       value: "remote-from-india" },
  { label: "OCI friendly — as stated in posting",         value: "oci-friendly" },
  { label: "Relocation sponsored — as stated in posting", value: "relocation-sponsored" },
  { label: "H-1B transfer — as stated in posting",        value: "h1b-transfer" },
];

const SCORE_OPTIONS = [
  { label: "Any",  value: 0 },
  { label: "50+",  value: 50 },
  { label: "70+",  value: 70 },
  { label: "85+",  value: 85 },
];

const PAGE_SIZE = 20;

// One pager control (page number, First/Last/Prev/Next). `current` teal-fills the
// active page; `disabled` greys out the ends. Consistent with the dark surface UI.
function PagerButton({ label, onClick, disabled = false, current = false }: {
  label: string; onClick: () => void; disabled?: boolean; current?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || current}
      aria-current={current ? "page" : undefined}
      style={{
        minWidth: "34px", padding: "7px 10px",
        background: current ? T.teal : T.s1,
        border: `1px solid ${current ? T.teal : T.b0}`,
        borderRadius: "6px", fontFamily: T.body, fontSize: "12px",
        color: current ? T.bg : disabled ? T.t3 : T.t1,
        fontWeight: current ? 600 : 400,
        cursor: disabled || current ? "default" : "pointer",
      }}
    >{label}</button>
  );
}

export function Matches() {
  const [searchParams]            = useSearchParams();
  const [workAuth,  setWorkAuth]  = useState("");
  // Initialise the score filter from ?minScore= so deep links (e.g. the
  // dashboard "Strong fit roles" tile → /matches?minScore=85) land pre-filtered.
  const [minScore,  setMinScore]  = useState(() => {
    const m = Number(searchParams.get("minScore"));
    return SCORE_OPTIONS.some(o => o.value === m && m > 0) ? m : 0;
  });
  const [page,      setPage]      = useState(0);
  const [selected,  setSelected]  = useState<MatchWithJob | null>(null);

  const q = (searchParams.get("q") ?? "").trim().toLowerCase();

  const filters = {
    limit:  PAGE_SIZE,
    offset: page * PAGE_SIZE,
    ...(workAuth && { workAuth }),
    ...(minScore && { minScore }),
  };
  const { data: pageData, isLoading, isFetching } = useMatchesPage(filters);
  const rawData    = pageData?.items;
  const total      = pageData?.total ?? 0;
  const totalPages = pageCount(total, PAGE_SIZE);

  // Guard against a stale page index after the total shrinks (e.g. filter change):
  // if the current page fell past the end, snap back to the last valid page.
  if (pageData && page > totalPages - 1 && page > 0) {
    setPage(clampPage(page, totalPages));
  }

  // Null-safe: a match row with a null/undefined title/company/source must not
  // throw during filter (that would crash the page → black screen). anyFieldIncludes
  // coerces every field safely.
  const data = q
    ? rawData?.filter(m => anyFieldIncludes([m.title, m.company, m.location, m.source], q))
    : rawData;

  const track = useApplyMutation();
  const save  = useSaveMutation();
  const { data: activeResume } = useActiveResume();
  const refresh = useGenerateMatches();
  // v1.1 PRODUCT_ANALYTICS_ENABLED: first time this browser sees at least one match.
  const firstCount = (pageData as { items?: unknown[] } | undefined)?.items?.length ?? 0;
  useEffect(() => { if (firstCount > 0) trackOnce("first_match_viewed"); }, [firstCount]);
  usePageMeta("Your matches — Caliber");

  return (
    <div style={{ maxWidth: "1280px", margin: "0 auto", width: "100%", minWidth: 0 }}>
      <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px", flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700, color: T.t0, margin: "0 0 6px" }}>
            Matches
          </h1>
          <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
            Roles scored for your resume. Click any card to open Role Intelligence.
          </p>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3, margin: "5px 0 0" }}>
            Matches outside tech roles are less tested.
          </p>
        </div>
        {/* Shown once the candidate already has matches — the manual, ALWAYS-
            recompute action. The first match set is generated automatically
            (no button) — see MatchReadinessPanel.tsx. */}
        {!!total && (
          <Btn
            variant="ghost" size="sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            {refresh.isPending ? "Refreshing…" : "Refresh Matches"}
          </Btn>
        )}
      </div>

      <ResumeClarityCallout clarity={activeResume?.clarity} to="/profile#target-role" />

      {/* Filters */}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "20px",
        alignItems: "center", padding: "12px 14px", background: T.s1,
        border: `1px solid ${T.b0}`, borderRadius: "8px" }}>
        <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
          letterSpacing: "0.08em", textTransform: "uppercase", marginRight: "4px" }}>Score</span>
        {SCORE_OPTIONS.map(o => (
          <Badge
            key={o.value}
            variant={minScore === o.value ? "teal" : "default"}
            size="sm"
            style={{ cursor: "pointer" }}
            onClick={() => { setMinScore(o.value); setPage(0); }}
          >{o.label}</Badge>
        ))}
        <div style={{ width: "1px", height: "16px", background: T.b1, margin: "0 4px" }}/>
        <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
          letterSpacing: "0.08em", textTransform: "uppercase", marginRight: "4px" }}>Auth</span>
        {WORK_AUTH_OPTIONS.map(o => (
          <Badge
            key={o.value}
            variant={workAuth === o.value ? "teal" : "default"}
            size="sm"
            style={{ cursor: "pointer" }}
            onClick={() => { setWorkAuth(o.value); setPage(0); }}
          >{o.label}</Badge>
        ))}
        {(isLoading || isFetching) && (
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, marginLeft: "auto" }}>
            loading…
          </span>
        )}
        {data && !isLoading && !q && (
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, marginLeft: "auto" }}>
            {total} result{total !== 1 ? "s" : ""}
          </span>
        )}
        {data && !isLoading && q && (
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.tealSoft, marginLeft: "auto" }}>
            Searching "{q}" · {data.length} result{data.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Responsive card grid — 1 column on mobile, 2–3+ as the viewport widens,
          so wide screens fill horizontally instead of one tall single column.
          The top match spans the full width as a prominent "best" banner.
          min(100%, 340px) floors the track at the container width so narrow
          phones (<340px) never overflow into a horizontal scroll. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))",
        gap: "12px",
        alignItems: "start",
      }}>
        {data?.map((m, i) => {
          const isBest = i === 0 && page === 0 && minScore === 0 && !q;
          return (
            <div key={m.matchId} style={isBest ? { gridColumn: "1 / -1" } : undefined}>
              <JobCard
                match={m}
                featured={isBest}
                onOpen={()  => setSelected(m)}
                onSave={()  => save.mutate(m)}
                onTrack={() => track.mutate(m)}
              />
            </div>
          );
        })}
      </div>
      {data?.length === 0 && !isLoading && (
        (workAuth || minScore || q)
          // A filter narrowed an otherwise non-empty result set to zero — this is
          // a filter problem, not a readiness problem, so the generic guidance
          // to loosen the filter stays as-is rather than showing readiness copy
          // that would be misleading here (the candidate DOES have matches).
          ? (
            <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "12px",
              padding: "40px", textAlign: "center" }}>
              <p style={{ fontFamily: T.display, fontSize: "16px", color: T.t1, margin: "0 0 6px" }}>
                No matches for this filter
              </p>
              <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
                Try lowering the score filter or clearing the search.
              </p>
            </div>
          )
          : <MatchReadinessPanel />
      )}

      {/* Pagination — numbered pages with First/Last (search is a within-page filter,
          so the pager is hidden while a search query is active). */}
      {!q && totalPages > 1 && (
        <nav aria-label="Matches pagination" style={{ display: "flex", gap: "6px",
          justifyContent: "center", marginTop: "24px", alignItems: "center", flexWrap: "wrap" }}>
          <PagerButton label="« First" onClick={() => setPage(0)}              disabled={page === 0} />
          <PagerButton label="‹ Prev"  onClick={() => setPage(p => p - 1)}     disabled={page === 0} />
          {pageWindow(page + 1, totalPages).map((tok, i) =>
            tok === "ellipsis"
              ? <span key={`e${i}`} style={{ fontFamily: T.mono, fontSize: "12px", color: T.t3, padding: "0 4px" }}>…</span>
              : <PagerButton key={tok} label={String(tok)} current={tok === page + 1} onClick={() => setPage(tok - 1)} />,
          )}
          <PagerButton label="Next ›"  onClick={() => setPage(p => p + 1)}     disabled={page >= totalPages - 1} />
          <PagerButton label="Last »"  onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} />
        </nav>
      )}

      {/* Role Intelligence Drawer */}
      <MatchDetailDrawer
        open={!!selected}
        match={selected}
        onClose={() => setSelected(null)}
        onTrack={() => { if (selected) track.mutate(selected); setSelected(null); }}
        onSave={()  => { if (selected) save.mutate(selected); }}
      />
    </div>
  );
}
